import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { formatNumber, getInitials } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ShoppingBag, Search, Plus, Heart, Loader2,
  Package, Shirt, Gem, Palette, Utensils, Smartphone, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  clothing: Shirt,
  accessories: Gem,
  art: Palette,
  food: Utensils,
  electronics: Smartphone,
  services: Briefcase,
  other: Package
};

export default function MarketplacePage() {
  const { token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/marketplace/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [token]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/marketplace/products?`;
      if (selectedCategory) url += `category=${selectedCategory}&`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}&`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedCategory, searchQuery]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSaveProduct = async (productId, isSaved) => {
    try {
      if (isSaved) {
        await axios.delete(`${API}/marketplace/products/${productId}/save`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/marketplace/products/${productId}/save`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setProducts(prev => prev.map(p => 
        p.id === productId ? { ...p, is_saved: !isSaved } : p
      ));
    } catch (error) {
      toast.error('Error');
    }
  };

  return (
    <div className="max-w-6xl mx-auto pb-20" data-testid="marketplace-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">Mache</h1>
          </div>
          <Button 
            onClick={() => navigate('/marketplace/sell')}
            className="rounded-full"
            data-testid="sell-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Vann
          </Button>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder={tr.searchProducts}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 rounded-full bg-secondary/50"
            data-testid="search-products"
          />
        </div>
      </header>

      {/* Categories */}
      <div className="px-4 py-4 overflow-x-auto">
        <div className="flex gap-2">
          <Button
            variant={selectedCategory === null ? "default" : "outline"}
            size="sm"
            className="rounded-full shrink-0"
            onClick={() => setSelectedCategory(null)}
          >
            {tr.all}
          </Button>
          {categories.map(cat => {
            const Icon = CATEGORY_ICONS[cat.id] || Package;
            return (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                size="sm"
                className="rounded-full shrink-0"
                onClick={() => setSelectedCategory(cat.id)}
                data-testid={`category-${cat.id}`}
              >
                <Icon className="w-4 h-4 mr-1" />
                {cat.name}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Products Grid */}
      <div className="px-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <ShoppingBag className="w-16 h-16 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">{tr.noProducts}</p>
            <p className="text-muted-foreground mt-2">{tr.startSelling}</p>
            <Button 
              onClick={() => navigate('/marketplace/sell')}
              className="mt-6 rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              {tr.listAProduct}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map(product => (
              <Link
                key={product.id}
                to={`/marketplace/product/${product.id}`}
                className="group bg-card rounded-2xl overflow-hidden border border-border/50 hover:border-primary/50 transition-all"
                data-testid={`product-${product.id}`}
              >
                {/* Image */}
                <div className="aspect-square bg-secondary/30 relative overflow-hidden">
                  {product.images?.[0] ? (
                    <img 
                      src={product.images[0]} 
                      alt={product.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}
                  
                  {/* Save button */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleSaveProduct(product.id, product.is_saved);
                    }}
                    className="absolute top-2 right-2 w-8 h-8 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center"
                    data-testid={`save-product-${product.id}`}
                  >
                    <Heart className={`w-4 h-4 ${product.is_saved ? 'fill-primary text-primary' : ''}`} />
                  </button>
                </div>
                
                {/* Info */}
                <div className="p-3">
                  <h3 className="font-semibold line-clamp-1">{product.title}</h3>
                  <p className="text-lg font-bold text-primary mt-1">${product.price.toFixed(2)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Avatar className="w-5 h-5">
                      <AvatarImage src={product.seller_avatar} />
                      <AvatarFallback className="text-[8px]">
                        {getInitials(product.seller_display_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-muted-foreground truncate">
                      @{product.seller_username}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
